export interface HolderData {
  address: string;
  balance: number;
  proportionOf100k: number;
}

// This function gets token holders from Helius API
export const getTokenHolders = async (): Promise<HolderData[]> => {
  try {
    // In production, you should keep the API key secured on the backend
    // and proxy the request through your server
    const url = `https://mainnet.helius-rpc.com/?api-key=${process.env.REACT_APP_HELIUS_API_KEY || '147cdcd5-d648-426a-ba4d-0765ed707cb3'}`;
    
    // For simplicity, we'll implement pagination on the client side
    // In a production app, you would want to implement server-side pagination
    let page = 1;
    let allHolders: HolderData[] = [];
    
    // Limit API calls to avoid exceeding rate limits for demo
    const maxPages = 2;
    
    while (page <= maxPages) {
      try {
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            jsonrpc: "2.0",
            method: "getTokenAccounts",
            id: "helius-test",
            params: {
              page: page,
              limit: 1000,
              displayOptions: {},
              mint: "H6dZStmJBb5c6YAsnV5JxTbgwJiLjT5zKc4GeBwcpump",
            },
          }),
        });

        if (!response.ok) {
          console.warn(`Page ${page} failed: ${response.status}`);
          break;
        }

        const data = await response.json();
        
        if (!data.result || data.result.token_accounts.length === 0) {
          console.log(`No more results. Total pages: ${page - 1}`);
          break;
        }
        
        // Extract holders with balances, skipping the specified address
        data.result.token_accounts.forEach((account: any) => {
          // Skip the specified address
          if (account.owner !== "GjkTe6az5xLXpe4NUAtEc1fLxoLkXDH62nUZBxHZtCoC") {
            allHolders.push({
              address: account.owner,
              balance: parseFloat(account.amount),
              proportionOf100k: 0 // Will calculate after getting total supply
            });
          }
        });
      } catch (pageError) {
        console.error(`Error in page ${page}:`, pageError);
        break;
      }
      
      page++;
    }
    
    // If we have no holders, return an empty array
    if (allHolders.length === 0) {
      return [];
    }
    
    // Sort by balance (descending)
    allHolders.sort((a, b) => b.balance - a.balance);
    
    // Calculate total supply (excluding the skipped address)
    const totalSupply = allHolders.reduce((sum, holder) => sum + holder.balance, 0);
    
    // Calculate proportion of 100K for each holder
    allHolders.forEach(holder => {
      holder.proportionOf100k = parseInt(((holder.balance / totalSupply) * 100000 * Math.pow(10, 6)).toFixed(0));
    });
    
    return allHolders;
  } catch (error) {
    console.error('Error fetching token holders:', error);
    // Return empty array instead of throwing
    return [];
  }
};

// Get aggregate statistics about token holders
export const getTokenHolderStats = (holders: HolderData[]) => {
  const totalHolders = holders.length;
  const totalSupply = holders.reduce((acc, holder) => acc + holder.balance, 0);
  const averageHolding = totalHolders > 0 ? totalSupply / totalHolders : 0;
  
  // Calculate distribution metrics
  let topTenHoldings = 0;
  if (holders.length >= 10) {
    topTenHoldings = holders.slice(0, 10).reduce((acc, holder) => acc + holder.balance, 0);
  }
  
  return {
    totalHolders,
    totalSupply,
    averageHolding,
    topTenPercent: totalSupply > 0 ? (topTenHoldings / totalSupply) * 100 : 0
  };
}; 